import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useMusicPlayer } from "../context/MusicPlayerContext";

const API_KEY = "AIzaSyDmncAyxuP66xJr9-nWbr0gG8j6YN8Ks0Q";

const Home = () => {
  const [songs, setSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("hindi");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { playSong, currentSong, isPlaying, handleNext, handlePrevious, currentTime, duration } = useMusicPlayer();

  // Fetch songs from YouTube (THIRD PARTY)
  const fetchSongs = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const res = await axios.get(
        "https://www.googleapis.com/youtube/v3/search",
        {
          params: {
            part: "snippet",
            q: searchTerm,
            type: "video",
            maxResults: 12,
            key: API_KEY,
          },
        }
      );

      const formatted = res.data.items.map((item) => ({
        id: item.id.videoId,
        title: item.snippet.title,
        artist: item.snippet.channelTitle,
        thumbnail: item.snippet.thumbnails.medium?.url || item.snippet.thumbnails.default?.url,
        videoId: item.id.videoId,
      }));

      setSongs(formatted);
    } catch (err) {
      console.error("YouTube API error:", err.response?.data || err.message);
      if (err.response?.status === 403) {
        setErrorMessage("API quota exceeded or invalid key. Please try again later.");
      } else {
        setErrorMessage("Failed to load songs. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchSongs();
  }, [fetchSongs]);

  // Play song using context
  const handlePlaySong = (song) => {
    console.log("Playing song:", song.title);
    playSong(song, songs);
  };

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    
    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        console.log('Swipe left - calling handleNext');
        handleNext();
      } else {
        console.log('Swipe right - calling handlePrevious');
        handlePrevious();
      }
    }
  };

  return (
    <div 
      className="min-h-screen bg-gray-900 text-white pb-28"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {currentSong && (
        <div className="w-full h-1 bg-gray-600 rounded-full cursor-pointer">
          <div
            className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-150"
            style={{ width: `${Math.min(100, (currentTime / (duration || 1)) * 100)}%` }}
          />
        </div>
      )}
      {/* HEADER */}
      <div className="p-6 border-b border-gray-700">
        <h1 className="text-3xl font-bold text-purple-400">
          🎵 Music Player
        </h1>
        <p className="text-gray-400">
          Songs loaded from YouTube
        </p>

        {/* SEARCH */}
        <div className="mt-4 flex gap-3">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchSongs()}
            placeholder="Search songs..."
            className="flex-1 px-4 py-3 rounded bg-gray-800 outline-none text-white placeholder-gray-400"
          />
          <button
            onClick={fetchSongs}
            className="px-6 py-3 bg-purple-600 rounded font-semibold hover:bg-purple-700 transition"
          >
            Search
          </button>
        </div>
      </div>

      {errorMessage && <p className="text-red-400 text-center mt-4">{errorMessage}</p>}

      {/* SONG LIST */}
      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading && (
          <div className="col-span-full flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        )}

        {!loading && songs.map((song) => (
          <div
            key={song.id}
            onClick={() => handlePlaySong(song)}
            className={`bg-gray-800 p-4 rounded-lg cursor-pointer hover:bg-gray-700 transition ${
              currentSong?.id === song.id ? 'ring-2 ring-purple-500' : ''
            }`}
          >
            <div className="relative">
              <img
                src={song.thumbnail}
                alt={song.title}
                className="rounded mb-3 w-full h-40 object-cover"
              />
              {/* Play overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity rounded-lg">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  currentSong?.id === song.id && isPlaying
                    ? 'bg-purple-500'
                    : 'bg-purple-600'
                }`}>
                  {currentSong?.id === song.id && isPlaying ? (
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
            <h3 className="font-semibold text-sm truncate text-white">
              {song.title}
            </h3>
            <p className="text-xs text-gray-400 truncate">
              {song.artist}
            </p>
          </div>
        ))}

        {!loading && songs.length === 0 && (
          <p className="text-gray-400 text-center col-span-full">No songs found</p>
        )}
      </div>
    </div>
  );
};

export default Home;
